/***************************************************************************
 * HoverRowActionsOverlay — portaled actions overlay(s) per DataGrid.
 *
 * Usually a single overlay is rendered at the right edge of either the
 * editing row (save/cancel/spinner) or the hovered row (pencil).
 *
 * During active editing, if the user hovers a DIFFERENT row (not the
 * editing row, not the just-saved row), a second overlay is rendered at
 * that hovered row's right edge showing a disabled pencil — so the user
 * sees a visual "can't edit this one right now" affordance while save /
 * cancel stay pinned to the row being edited.
 ***************************************************************************/

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {createPortal} from "react-dom";
import {GridApi} from "@mui/x-data-grid-pro";
import {EditRowActions} from "./EditRowActions";
import {useRowEditContext} from "./RowEditContext";

interface Props
{
   apiRef: React.MutableRefObject<GridApi>;
   isRowEditable: boolean;
   /** When set, rows are NOT editable but the pencil still renders on hover — disabled,
    *  with this message as its tooltip.  Used to explain an ambient reason editing is
    *  unavailable (e.g. the parent record on a RecordScreen is in edit mode). */
   rowEditDisabledMessage?: string;
   onEnterEdit: (rowId: string | number) => void;
   /** Whether rows can be deleted — renders a trash icon after the pencil. */
   isRowDeletable?: boolean;
   /** Fires when a row's trash icon is clicked. */
   onDeleteClick?: (rowId: string | number) => void;
   /** When set, rows are NOT deletable but the trash still renders on hover — disabled,
    *  with this message as its tooltip (mirrors rowEditDisabledMessage). */
   deleteDisabledReason?: string;
}

function computeOverlayStyle(
   rootEl: HTMLElement | null,
   apiRef: React.MutableRefObject<GridApi>,
   rowId: string | number | null,
): React.CSSProperties
{
   if (!rootEl || rowId == null)
   {
      return {display: "none"};
   }
   const rowEl = apiRef.current?.getRowElement?.(rowId) as HTMLElement | null;
   if (!rowEl)
   {
      return {display: "none"};
   }
   const rootRect = rootEl.getBoundingClientRect();
   const rowRect  = rowEl.getBoundingClientRect();
   const top      = rowRect.top - rootRect.top;
   const height   = rowRect.height;

   ////////////////////////////////////////////////////////////////////////////////
   // Hide when the target row has been scrolled out of the grid's viewport —    //
   // otherwise icons would continue floating where the row WOULD have been.     //
   ////////////////////////////////////////////////////////////////////////////////
   const scroller = rootEl.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement | null;
   const viewport = scroller ? scroller.getBoundingClientRect() : rootRect;
   const rowBottom = rowRect.top + rowRect.height;
   if (rowBottom <= viewport.top || rowRect.top >= viewport.bottom)
   {
      return {display: "none"};
   }

   return {
      position:      "absolute",
      top,
      height,
      right:         20,
      display:       "flex",
      alignItems:    "center",
      ////////////////////////////////////////////////////////////////////////////////
      // Low z-index so the grid's header (column headers sticky at top) and footer //
      // (pagination bar) visually cover the icons when a row is partially behind   //
      // the chrome.                                                                //
      ////////////////////////////////////////////////////////////////////////////////
      zIndex:        1,
      pointerEvents: "none",
   };
}


export const HoverRowActionsOverlay: React.FC<Props> = ({apiRef, isRowEditable, onEnterEdit, rowEditDisabledMessage, isRowDeletable, onDeleteClick, deleteDisabledReason}) =>
{
   const ctx = useRowEditContext();
   const editingRowId = ctx?.editingRowId ?? null;
   const savedRowId = ctx?.savedRowId ?? null;

   const [rootEl, setRootEl] = useState<HTMLElement | null>(null);
   const [hoveredRowId, setHoveredRowId] = useState<string | number | null>(null);
   const [primaryStyle, setPrimaryStyle] = useState<React.CSSProperties>({display: "none"});
   const [disabledPencilStyle, setDisabledPencilStyle] = useState<React.CSSProperties>({display: "none"});

   //////////////////////////////////////////////////////////////////////////////////
   // Primary overlay target:                                                       //
   //   - editing row when an edit is active (save/cancel stay pinned)              //
   //   - else hovered row (normal pencil reveal)                                   //
   // Saved-row badge is handled by the separate SavedRowOverlay portal.            //
   //                                                                              //
   // Secondary overlay target (disabled pencil):                                   //
   //   - the hovered row, only when edit is active on a DIFFERENT row and that     //
   //     hovered row is not the just-saved one.                                    //
   //////////////////////////////////////////////////////////////////////////////////
   const primaryRowId = editingRowId != null ? editingRowId : hoveredRowId;
   const showDisabledPencil = editingRowId != null
      && hoveredRowId != null
      && hoveredRowId !== editingRowId
      && hoveredRowId !== savedRowId;
   const disabledPencilRowId = showDisabledPencil ? hoveredRowId : null;

   ///////////////////////////////////////////////////////////////////////////////////
   // Resolve the grid's root element on mount.                                      //
   ///////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      let cancelled = false;
      function tryResolve()
      {
         const root = apiRef.current?.rootElementRef?.current as HTMLElement | undefined;
         if (root)
         {
            setRootEl(root);
            return true;
         }
         return false;
      }
      if (!tryResolve())
      {
         const id = setInterval(() =>
         {
            if (cancelled)
            {
               return;
            }
            if (tryResolve())
            {
               clearInterval(id);
            }
         }, 50);
         return () =>
         {
            cancelled = true;
            clearInterval(id);
         };
      }
   }, [apiRef]);


   ///////////////////////////////////////////////////////////////////////////////////
   // DOM listeners on the grid root: mouseover identifies the row under the cursor; //
   // mouseleave clears hover state so the overlay hides when the pointer leaves.    //
   ///////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (!rootEl)
      {
         return;
      }

      function findRowElementAncestor(el: EventTarget | null): HTMLElement | null
      {
         let node = el as HTMLElement | null;
         while (node && node !== rootEl)
         {
            if (node.classList && node.classList.contains("MuiDataGrid-row"))
            {
               return node;
            }
            node = node.parentElement;
         }
         return null;
      }

      function onMouseOver(e: MouseEvent)
      {
         const rowEl = findRowElementAncestor(e.target);
         if (!rowEl)
         {
            return;
         }
         const rowIdAttr = rowEl.getAttribute("data-id");
         if (rowIdAttr == null)
         {
            return;
         }
         const parsed = /^-?[0-9]+$/.test(rowIdAttr) ? Number(rowIdAttr) : rowIdAttr;
         setHoveredRowId((prev) => (prev === parsed ? prev : parsed));
      }

      function onMouseLeave()
      {
         setHoveredRowId(null);
      }

      rootEl.addEventListener("mouseover", onMouseOver);
      rootEl.addEventListener("mouseleave", onMouseLeave);
      return () =>
      {
         rootEl.removeEventListener("mouseover", onMouseOver);
         rootEl.removeEventListener("mouseleave", onMouseLeave);
      };
   }, [rootEl]);

   const updateStyles = useCallback(() =>
   {
      setPrimaryStyle(computeOverlayStyle(rootEl, apiRef, primaryRowId));
      setDisabledPencilStyle(computeOverlayStyle(rootEl, apiRef, disabledPencilRowId));
   }, [apiRef, rootEl, primaryRowId, disabledPencilRowId]);

   useLayoutEffect(() =>
   {
      updateStyles();
   }, [updateStyles]);

   ///////////////////////////////////////////////////////////////////////////////////
   // Forced-hover class on the primary target row so the row's background stays    //
   // highlighted while the mouse hovers the overlay (which is outside the row DOM).//
   // DataGridPro with pinned columns renders 3 row elements per logical row; add   //
   // the class to every one so the whole row highlights consistently.              //
   ///////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (primaryRowId == null)
      {
         return;
      }
      const root = rootEl;
      if (!root)
      {
         return;
      }
      const rows = Array.from(root.querySelectorAll(`.MuiDataGrid-row[data-id="${CSS.escape(String(primaryRowId))}"]`)) as HTMLElement[];
      if (rows.length === 0)
      {
         return;
      }
      rows.forEach((r) => r.classList.add("qqq-row-edit-forced-hover"));
      return () =>
      {
         rows.forEach((r) => r.classList.remove("qqq-row-edit-forced-hover"));
      };
   }, [rootEl, primaryRowId]);

   useEffect(() =>
   {
      if (!rootEl)
      {
         return;
      }
      const ro = new ResizeObserver(updateStyles);
      ro.observe(rootEl);
      const scroller = rootEl.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement | null;
      if (scroller)
      {
         scroller.addEventListener("scroll", updateStyles);
      }
      window.addEventListener("resize", updateStyles);
      return () =>
      {
         ro.disconnect();
         if (scroller)
         {
            scroller.removeEventListener("scroll", updateStyles);
         }
         window.removeEventListener("resize", updateStyles);
      };
   }, [rootEl, updateStyles]);

   ///////////////////////////////////////////////////////////////////////////////////
   // When the editing row changes, give DataGrid a tick to re-render then           //
   // reposition the overlays.                                                       //
   ///////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      const t = setTimeout(updateStyles, 0);
      return () => clearTimeout(t);
   }, [editingRowId, updateStyles]);

   const primaryContent = useMemo(() =>
   {
      if (primaryRowId == null)
      {
         return null;
      }
      if (!isRowEditable && !isRowDeletable && editingRowId == null && !rowEditDisabledMessage && !deleteDisabledReason)
      {
         return null;
      }
      ////////////////////////////////////////////////////////////////////////////
      // When the user hovers the row that just got saved, the SavedRowOverlay  //
      // already owns that row's right-edge badge.  Skip the pencil here so the //
      // two don't stack on top of each other.                                  //
      ////////////////////////////////////////////////////////////////////////////
      if (editingRowId == null && savedRowId != null && primaryRowId === savedRowId)
      {
         return null;
      }
      return (
         <div style={primaryStyle} className="qqq-row-edit-actions-overlay">
            <div style={{pointerEvents: "auto"}}>
               <EditRowActions rowId={primaryRowId} isRowEditable={isRowEditable} onEnterEdit={onEnterEdit} disabledReason={rowEditDisabledMessage} isRowDeletable={isRowDeletable} onDeleteClick={onDeleteClick} deleteDisabledReason={deleteDisabledReason} />
            </div>
         </div>
      );
   }, [primaryRowId, isRowEditable, editingRowId, savedRowId, primaryStyle, onEnterEdit, rowEditDisabledMessage, isRowDeletable, onDeleteClick, deleteDisabledReason]);

   /***************************************************************************
    * Disabled-pencil overlay for a different row hovered during edit.  Rendering
    * <EditRowActions> with the hovered row's id (which is NOT the editing row) lets
    * that component's existing isEditingOtherRow branch produce the greyed-out
    * tooltipped pencil.
    ***************************************************************************/
   const disabledPencilContent = useMemo(() =>
   {
      if (disabledPencilRowId == null)
      {
         return null;
      }
      if (!isRowEditable && !isRowDeletable)
      {
         return null;
      }
      return (
         <div style={disabledPencilStyle} className="qqq-row-edit-actions-overlay qqq-row-edit-actions-overlay--disabled">
            <div style={{pointerEvents: "auto"}}>
               <EditRowActions rowId={disabledPencilRowId} isRowEditable={isRowEditable} onEnterEdit={onEnterEdit} isRowDeletable={isRowDeletable} />
            </div>
         </div>
      );
   }, [disabledPencilRowId, isRowEditable, disabledPencilStyle, onEnterEdit, isRowDeletable]);

   if (!rootEl)
   {
      return null;
   }
   const merged = (
      <>
         {primaryContent}
         {disabledPencilContent}
      </>
   );
   return createPortal(merged, rootEl);
};
